import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Grid3x3, BarChart3, FileText, ClipboardList, Folder, ChevronDown, Zap } from 'lucide-react';
export function AddItemDropdown({ onCreateItem, currentFolder, onAddApp }) {
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownStyle, setDropdownStyle] = useState({});
    const dropdownRef = useRef(null);
    const buttonRef = useRef(null);
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);
    const updateDropdownPosition = () => {
        if (buttonRef.current && isOpen) {
            const rect = buttonRef.current.getBoundingClientRect();
            const dropdownHeight = 400; // Estimated dropdown height
            const dropdownWidth = 288; // w-72 = 288px
            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;
            // Calculate optimal position
            let top = rect.bottom + 8;
            let left = rect.left;
            // Check if dropdown would go below viewport
            if (top + dropdownHeight > viewportHeight) {
                // Position above the button instead
                top = rect.top - dropdownHeight - 8;
            }
            // Check if dropdown would go beyond right edge
            if (left + dropdownWidth > viewportWidth) {
                // Align to right edge of button
                left = rect.right - dropdownWidth;
            }
            // Ensure dropdown doesn't go beyond left edge
            if (left < 8) {
                left = 8;
            }
            // Ensure dropdown doesn't go above top of viewport
            if (top < 8) {
                top = 8;
            }
            setDropdownStyle({
                position: 'fixed',
                top,
                left,
                zIndex: 9999,
                maxHeight: Math.min(dropdownHeight, viewportHeight - 16),
                overflowY: 'auto',
            });
        }
    };
    useEffect(() => {
        if (isOpen) {
            updateDropdownPosition();
            window.addEventListener('scroll', updateDropdownPosition);
            window.addEventListener('resize', updateDropdownPosition);
        }
        return () => {
            window.removeEventListener('scroll', updateDropdownPosition);
            window.removeEventListener('resize', updateDropdownPosition);
        };
    }, [isOpen]);
    const handleItemClick = (type) => {
        onCreateItem(type);
        setIsOpen(false);
    };
    const createItems = [
        {
            type: 'board',
            icon: Grid3x3,
            label: 'Board',
            description: 'Manage tasks and projects',
            color: 'text-blue-600'
        },
        {
            type: 'dashboard',
            icon: BarChart3,
            label: 'Dashboard',
            description: 'Visualize data and metrics',
            color: 'text-purple-600'
        },
        {
            type: 'doc',
            icon: FileText,
            label: 'Doc',
            description: 'Create documentation',
            color: 'text-green-600'
        },
        {
            type: 'form',
            icon: ClipboardList,
            label: 'Form',
            description: 'Collect data and responses',
            color: 'text-orange-600'
        },
        {
            type: 'folder',
            icon: Folder,
            label: 'Folder',
            description: 'Organize your workspace',
            color: 'text-gray-600'
        }
    ];
    return (_jsxs("div", { className: "relative", ref: dropdownRef, children: [_jsxs("button", { ref: buttonRef, onClick: () => setIsOpen(!isOpen), className: "flex items-center space-x-2 px-3 py-2 bg-white/10 text-adaptive-primary rounded-lg hover:bg-white/15 transition-colors", children: [_jsx(Plus, { className: "w-4 h-4" }), _jsx("span", { className: "text-sm font-medium", children: "Add" }), _jsx(ChevronDown, { className: `w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}` })] }), isOpen && createPortal(_jsx("div", { className: "w-72 liquid-glass-sidebar rounded-lg shadow-lg min-w-max", style: dropdownStyle, children: _jsxs("div", { className: "py-2 max-h-full overflow-y-auto", children: [_jsx("div", { className: "px-3 py-2 text-xs font-medium text-adaptive-muted uppercase tracking-wide border-b border-white/10", children: currentFolder ? `Add to ${currentFolder}` : 'Add to workspace' }), _jsx("div", { className: "py-2", children: createItems.map((item) => {
                                const Icon = item.icon;
                                return (_jsxs("button", { onClick: () => handleItemClick(item.type), className: "w-full flex items-center px-3 py-3 hover:bg-white/05 transition-colors group", children: [_jsx("div", { className: `p-2 rounded-lg ${item.color} bg-white/10 group-hover:bg-white/15 mr-3`, children: _jsx(Icon, { className: "w-4 h-4" }) }), _jsxs("div", { className: "flex-1 text-left", children: [_jsx("div", { className: "text-sm font-medium text-adaptive-primary", children: item.label }), _jsx("div", { className: "text-xs text-adaptive-muted", children: item.description })] })] }, item.type));
                            }) }), _jsxs("div", { className: "border-t border-white/10 pt-2", children: [onAddApp && (_jsxs("button", { onClick: () => {
                                        onAddApp();
                                        setIsOpen(false);
                                    }, className: "w-full flex items-center px-3 py-3 hover:bg-gray-50 transition-colors group", children: [_jsx("div", { className: "p-2 rounded-lg text-purple-400 bg-white/10 group-hover:bg-white/15 mr-3", children: _jsx(Zap, { className: "w-4 h-4" }) }), _jsxs("div", { className: "flex-1 text-left", children: [_jsx("div", { className: "text-sm font-medium text-adaptive-primary", children: "Add App" }), _jsx("div", { className: "text-xs text-adaptive-muted", children: "Install an app to workspace" })] })] })), _jsx("div", { className: "px-3 py-2", children: _jsx("button", { className: "w-full text-left text-xs text-adaptive-muted hover:text-adaptive-secondary", children: "Browse template center" }) })] })] }) }), document.body)] }));
}
